import {Button, ButtonSpinner, Checkbox, Form, Input, Locale, Select, Slideout, TextArea} from '@local/ui/components'
import CloseSVG from '@local/assets/svgs/icon-close.svg'
import {useRef, useState} from 'react'
import {EMAIL_PATTERN, emailIsValid} from '@local/utils/string'
import {useTranslate} from '@local/hooks'
import PhoneNumberInput from '@local/ui/components/phoneNumberInput/PhoneNumberInput'
import {usePathname} from 'next/navigation'
import {post} from '@local/utils/fetch'
import ContactForm from '@local/ui/components/flodesk/ContactForm'

interface Props {
	listID: string
	description: string
	successText: string
	successTitle: string
	retreatList: Array<{ label: string, id: string, value: string }>
	isOpen: boolean
	title: string
	onClickClose: () => void
	openCalendly?: () => void
}

const TYPE_OPTIONS = [
	{id: 'individual', label: 'Individual', value: 'individual'},
	{id: 'business', label: 'Business', value: 'business'},
]

const NOT_INTERESTED_OPTION = {label: 'No Experience of Interest', value: 'no-experience-of-interest', id: 'no-experience-of-interest'}

export const EnquireForm = ({isOpen = false, onClickClose, openCalendly, listID, title, description, retreatList, successText, successTitle}: Props) => {
	const locale = useTranslate()
	const [email, setEmail] = useState<string>('')
	const [type, setType] = useState<string>('')
	const [retreat, setRetreat] = useState<string>('')
	const [isLoading, setIsLoading] = useState<boolean>(false)
	const [hasError, setHasError] = useState<boolean>(false)
	const [onPhoneError, setOnPhoneError] = useState<boolean>(false)
	const [phoneNumber, setPhoneNumber] = useState<string>('')
	const [firstName, setFirstName] = useState<string>('')
	const [lastName, setLastName] = useState<string>('')
	const [allowSubscribe, setAllowSubscribe] = useState<boolean>(false)
	const [subscribtionStatus, setSubscribtionStatus] = useState<string>('notAllowed')
	const [captchaShown, setCaptchaShown] = useState<boolean>(false)
	const buttonRef = useRef<HTMLButtonElement>(null)
	const pathname = usePathname()

	const handleSubmit = async ({body}) => {
		try {
			setHasError(false)
			setOnPhoneError(false)
			setIsLoading(true)

			if (!emailIsValid(body.email)) {
				throw new Error('InvalidEmail')
			}

			const res = await fetch('/api/enquire', {
				method: 'POST',
				body: JSON.stringify({
					listID,
					firstName: body.firstName,
					lastName: body.lastName,
					retreat: body.retreat,
					emailAddress: body.email,
					phoneNumber: body.countryCode + body.phoneNumber,
					message: body.message,
					type: body.type,
					company: body.company || 'individual',
					subscribe: body.subscribeMail ? 'yes' : 'no',
				}),
			})

			const result = await res.json()
			if (res.status === 200) {
				post('/api/fb-conversion', {
					event: 'Contact',
					pathname,
					data: {
						firstName: body.firstName,
						lastName: body.lastName,
						email: body.email,
						phoneNumber: body.countryCode + body.phoneNumber,
						type: body.type,
						company: body.company || 'individual',
						retreat: body.retreat,
						subscribeMail: body.subscribeMail ? 'yes' : 'no',
					},
				})
			}
			if (res.status === 200) {
				if (body.subscribeMail) {
					buttonRef.current?.click()
					setCaptchaShown(true)
				}

				setIsLoading(false)
				return {status: 200}
			} else {
				setIsLoading(false)
				setHasError(true)
				return {errors: [result]}
			}
		} catch (error) {
			setIsLoading(false)
			setHasError(true)

			if (error.message === 'InvalidEmail') {
				return {errors: [{code: 400, message: 'Invalid email'}]}
			}
			if (error.message === 'RequestFailed') {
				return {errors: [{code: 500, message: 'Request failed'}]}
			}
		}
	}

	const retreatOptions = [NOT_INTERESTED_OPTION, ...retreatList.filter(Boolean)]
	return (
		<Slideout
			isOpen={isOpen}
			variant='right'
			onClickClose={onClickClose}
			className='bg-grey-4 text-white relative !px-0'>
			<div data-lenis-prevent className='overflow-y-scroll min-h-screen h-fit margin-px-1 flex flex-col scrollbar-hide'>
				<div className='sticky top-0 z-1 flex items-center justify-between w-full py-10 lg:py-20 bg-grey-4'>
					<h2 className='w-fit uppercase'>{locale(title) || 'Enquire'}</h2>
					<Button
						small
						variant='icon'
						onClick={onClickClose}
						className='px-16 aspect-square'>
						<CloseSVG />
					</Button>
				</div>
				<Form
					onSubmit={handleSubmit}
					autoReset
					hideSuccess={subscribtionStatus === 'default'}
					successMessage={<SuccessPanel title={successTitle} text={successText} openCalendly={openCalendly} />}
					errorClassName='bottom-8 text-center'
					successClassName='z-5 mt-0 -inset-1 w-[calc(100%+2px)]'
					className='flex flex-col  w-full pt-40 gap-14'>
					<Select
						required
						theme='dark-grey'
						name='type'
						onChange={(e) => setType(e.target.value)}
						value={type}
						options={TYPE_OPTIONS}
						placeholder={locale('Type')} />
					<Input
						required
						variant='dark'
						type='text'
						name='firstName'
						label={locale('First Name')}
						value={firstName}
						onChange={(e) => setFirstName(e.target.value)}
						className='h-48'
						inputClassName='h-full'
						errorClassName='z-10 bg-grey-4'
						labelClassName='bg-grey-4' />
					<Input
						required
						variant='dark'
						type='text'
						name='lastName'
						value={lastName}
						onChange={(e) => setLastName(e.target.value)}
						label={locale('Last Name')}
						className='h-48'
						inputClassName='h-full'
						errorClassName='z-10 bg-grey-4'
						labelClassName='bg-grey-4' />
					{type === 'business' &&
						<Input
							required={type === 'business'}
							variant='dark'
							type='text'
							name='company'
							label={locale('Company')}
							className='h-48'
							inputClassName='h-full'
							errorClassName='z-10 bg-grey-4'
							labelClassName='bg-grey-4' />}
					<PhoneNumberInput
						onChange={(value: string) => setPhoneNumber(value)}
						onInvalid={() => setOnPhoneError(true)}
						hasError={onPhoneError} />

					<Input
						variant='dark'
						type='email'
						name='email'
						label='Email'
						value={email}
						onChange={(e) => setEmail(e.target.value)}
						onInvalid={() => setHasError(true)}
						pattern={EMAIL_PATTERN}
						hasError={hasError}
						className='h-48'
						inputClassName='h-full'
						errorClassName='z-10 bg-grey-4'
						labelClassName='bg-grey-4' />

					{retreatList.length > 0 &&
						<Select
							required
							theme='dark-grey'
							name='retreat'
							onChange={(e) => setRetreat(e.target.value)}
							value={retreat}
							options={retreatOptions}
							placeholder={locale('Experience you are interested in')} />}
					<TextArea
						required
						variant='dark'
						name='message'
						label={locale('Your message to us')}
						inputClassName='h-full'
						className='h-72 col-span-2'
						errorClassName='z-10 bg-grey-4'
						labelClassName='bg-grey-4' />
					<span className='text-10 text-white'>{description}</span>
					<Checkbox
						checked={allowSubscribe}
						name='subscribeMail'
						theme='dark'
						className='col-span-2'
						onChange={() => {
							setAllowSubscribe(!allowSubscribe)
							if (subscribtionStatus === 'default') {
								setSubscribtionStatus('notAllowed')
							} else {
								setSubscribtionStatus('default')
							}
						}}>
						<span className='text-12 text-white'>Receive inspiration in your inbox</span>
					</Checkbox>
					{!captchaShown &&
						<ButtonSpinner
							type='submit'
							small
							loading={isLoading}
							variant='primary-transparent'
							className='mt-auto mb-24'>
							<Locale>Submit</Locale>
						</ButtonSpinner>}
				</Form>
				{subscribtionStatus !== 'notAllowed' && <ContactForm buttonRef={buttonRef} firstName={firstName} lastName={lastName} phoneNumber={phoneNumber} email={email} setStatus={setSubscribtionStatus} />}
			</div>
		</Slideout>
	)
}

const SuccessPanel = ({title, text, openCalendly}) => {
	return (
		<div className='flex flex-col items-start pt-40 w-full h-full gap-14 bg-grey-4 normal-case text-left'>
			<h3 className='text-32 font-serif tracking-tight'>{title}</h3>
			<p className='text-12 text-grey-1 '>{text}</p>
			{openCalendly && <Button variant='secondary' onClick={openCalendly} className='w-full h-44'>BOOK YOUR FREE 1:1 CALL</Button>}
		</div>
	)
}
